import { Injectable, NgZone, OnDestroy } from "@angular/core";

import { AngularFireAuth } from "@angular/fire/auth";

import { Observable } from 'rxjs';
import { Router } from '@angular/router';
import { Paciente } from '../interfaces/pacient-info.interface';
import { Medico } from '../interfaces/medico.interface';
import { RolesManagerService } from '../guards/roles-manager.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { Secretaria } from '../interfaces/secretaria.interface';
import { AlertsService } from 'app/shared/alerts.service';
import { APIGestaCardService, OrgType } from '../api/gestacard.service';
import { Usuario } from '../interfaces/usuario.interface';
import { OverlayService } from 'app/shared/overlay.service';
import { Organizacao } from "../interfaces/organizacao.interface";
import { takeUntil, take } from 'rxjs/operators';
import { logger } from '../utils/console';

interface User {
    uid: string;
    email: string;
    displayName?: string;
}

@Injectable({
    providedIn: "root",
})
export class AuthService {
  email: string;

  user: Observable<Usuario>;
  tipo_usuario: string;
  usuario: Usuario;
  organizacao: Organizacao;
  usuario_as_obs: BehaviorSubject<Usuario | Paciente>; 
  organizacao_as_obs: BehaviorSubject<Organizacao>;
  loginCompleted: Subject<boolean>; // Emite quando o login é concluído com sucesso
  private destroy$ = new Subject<void>(); // Para gerenciar unsubscribe
  updateObservable: Function; 

  constructor(
    private auth: AngularFireAuth, 
    private router: Router, 
    private _apigestacard: APIGestaCardService, 
    private rolesManagerService: RolesManagerService, 
    private ngZone: NgZone,
    private snackService: AlertsService,
    private overlayService: OverlayService
  ) { 
    this.usuario_as_obs = new BehaviorSubject<Usuario>(Paciente.createEmpty()); 
    this.organizacao_as_obs = new BehaviorSubject<Organizacao>(Organizacao.createEmpty());
    this.loginCompleted = new Subject<boolean>(); 

    // Apenas setup de callbacks globais no construtor
    window.my = window.my || {};
    window.my.namespace = window.my.namespace || {};
    window.my.namespace.Logout = this.PublicLogout.bind(this);
  }

  /**
   * Inicializa o serviço de autenticação
   * Deve ser chamado pelo APP_INITIALIZER após a aplicação estar pronta
   */
  init(): void {
    const token = localStorage.getItem('accessToken');
    const currentUrl = window.location.href;
    
    // Só tenta buscar usuário se tiver token e não estiver em rotas públicas
    if (token && currentUrl.indexOf('webview') === -1 && currentUrl.indexOf('auth') === -1) {
      this.user = this._apigestacard.GetMeObs();
      this.LoginGetMe();
    }
  }

  LoginGetMe() {
    this._apigestacard.GetMeObs().pipe(
      take(1), // Completa após primeira emissão
      takeUntil(this.destroy$)
    ).subscribe(async usuario => {
      var permissao = usuario.tipo_usuario;

      this.usuario = usuario; 

      this.usuario_as_obs.next(usuario);

      await this.rolesManagerService.SetarAmbiente(permissao); 

      this._apigestacard.PegarOrganizacaoLogada().then((org) => {
        if (!org) {
          logger.error('[AuthService] Organização não encontrada para o usuário logado');
          this.snackService.SnackMessage('Organização do usuário não encontrada. Contate o suporte.');
          return;
        }
        this.organizacao = org; 
        this.organizacao_as_obs.next(org); 
      })

    }, () => {
      this.Logout()
    })
  }

  GetLoginOrgs(email: string, senha: string): Promise<OrgType[]> {
    return this._apigestacard.GetOrgsForLogin(email, senha);
  }

  Login(email: string, senha: string, orgId?: string): Promise<Usuario> {
    return new Promise(async (resolve, reject) => {
      try{
        localStorage.setItem(`medicoLogado`, 'sim')
        
        const dadosLogin = await this._apigestacard.signInWithEmailAndPassword(email, senha, orgId)

        if (this._apigestacard.isCodeType(dadosLogin)) {
          this.email = email

          this.snackService.SnackMessage('Digite o código de verificação')

          if (dadosLogin.needCode) this.router.navigateByUrl(`/auth/alter-password`)

          this.overlayService.hide(); 

        } else {
        
          var permissao = dadosLogin.data.permissao; 
          
          var dados_usuario = await this.BuscarDadosDoUsuario(email, permissao, dadosLogin.accessToken); 
          
          this.usuario = dados_usuario; 
          this.usuario_as_obs.next(dados_usuario); 
          
          var setando_ambiente = await this.rolesManagerService.SetarAmbiente(permissao);

          this._apigestacard.PegarOrganizacaoLogada().then((org) => {
            if (!org) {
              logger.error('[AuthService] Organização não encontrada para o usuário logado');
              this.snackService.SnackMessage('Organização do usuário não encontrada. Contate o suporte.');
              reject({ message: 'Organização do usuário não encontrada. Contate o suporte.' });
              return;
            }
            this.organizacao = org; 
            this.organizacao_as_obs.next(org); 
          })

          if(setando_ambiente == 'sucesso') {
            // Emite evento de login concluído após pequeno delay para garantir que tudo foi inicializado
            setTimeout(() => {
              this.loginCompleted.next(true);
            }, 100);
            resolve(dados_usuario);
          } else {
            resolve(null);
          } 
        } 

      } catch(err) {

        var aux = { message: err };
        
        reject(aux);

      }
    });
  }

  LoginByKey(accessToken: string): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        logger.log('[AuthService] Iniciando LoginByKey');
        
        // Verifica se já estava logado para atualizar ambiente
        const jaEstavaLogado = localStorage.getItem('accessToken');

        // 1. Valida token e pega dados básicos do usuário
        var usuario = await this._apigestacard.signInWithToken(accessToken);
        logger.log('[AuthService] Token validado, usuário:', usuario.email);

        // 2. Busca dados completos do usuário
        var permissao = usuario.tipo_usuario; 
        var dados_usuario = await this.BuscarDadosDoUsuario(usuario.email, permissao, accessToken); 
        logger.log('[AuthService] Dados do usuário carregados');

        // 3. Atualiza estado global (já atualiza UI reativamente)
        this.usuario = dados_usuario; 
        this.usuario_as_obs.next(dados_usuario); 
        
        // 4. Configura ambiente/tema baseado no tipo de usuário
        // RolesManagerService.SetarAmbiente() já aplica o tema E navega para rota correta
        var setando_ambiente = await this.rolesManagerService.SetarAmbiente(permissao); 
        logger.log('[AuthService] Ambiente configurado:', permissao);

        // 5. Carrega organização
        var org = await this._apigestacard.PegarOrganizacaoLogada();
        if (!org) {
          logger.error('[AuthService] Organização não encontrada para o usuário logado');
          reject({ message: 'Organização do usuário não encontrada. Contate o suporte.' });
          return;
        }
        this.organizacao = org; 
        this.organizacao_as_obs.next(org);
        logger.log('[AuthService] Organização carregada');
        
        // NÃO precisa de reload! O Angular atualiza reativamente:
        // - usuario_as_obs.next() notifica todos os subscribers (guards, components)
        // - RolesManagerService já aplicou o tema via FuseConfigService
        // - RolesManagerService já navegou para a rota correta
        // - loginCompleted.next() vai carregar o menu
        
        if (jaEstavaLogado) {
          logger.log('[AuthService] Usuário já estava logado - ambiente atualizado sem reload');
        }

        // Emite evento de login concluído para carregar o menu
        if (setando_ambiente === 'sucesso') {
          setTimeout(() => {
            this.loginCompleted.next(true);
            logger.log('[AuthService] Login concluído com sucesso');
          }, 100);
        }

        resolve();

      } catch (err) {
        logger.error('[AuthService] Erro no LoginByKey:', err);
        
        var message = '';
        if (typeof err === 'string') {
          message = err;
        } else if (err && err.error && err.error.message) {
          message = err.error.message;
        } else if (err && err.message) {
          message = err.message;
        }

        // Mensagens específicas por tipo de erro
        if (!message) {
          if (err && err.status === 401) {
            message = 'Link expirado ou inválido. Solicite um novo link.';
          } else if (err && err.status === 403) {
            message = 'Você não tem permissão para acessar este conteúdo.';
          } else if (err && err.status === 404) {
            message = 'Usuário não encontrado.';
          } else {
            message = 'Erro ao fazer login. Verifique sua conexão e tente novamente.';
          }
        }

        // NÃO faz logout automático - permite que usuário tente novamente
        // this.Logout();
        
        reject({ message });
      }
    });
  }

  Logout(redirect = true) {
    this.usuario = null; 
    this.usuario_as_obs.next(null);
    this.organizacao_as_obs.next(null);
    localStorage.removeItem('medicoLogado')
    localStorage.removeItem('secretariaHash')
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')

    if (redirect) this.router.navigateByUrl('/auth/login')
  }

  VerificarCodigo(codigo: string): Promise<Boolean> {

    return this._apigestacard.VerificarCodigo(this.email, codigo);
  
  }

  ChecarUsuario(email: string): Promise<Boolean> {
    return this._apigestacard.ChecarUsuario(email);
  }

  EnviarCodigo(email: string): Promise<Boolean> {
    return this._apigestacard.EnviarCodigo(email);
  }

  ChangePassword(email: string, codigo: string, senha: string): Promise<Boolean> {

    return this._apigestacard.AtualizaSenha(email, codigo, senha);

  }

  PublicLogout() {
    this.ngZone.run(() => this.Logout()); 
  }

  Register() {
    this._apigestacard.GetMeObs().subscribe((res) => {
       
    })
  }

  EnviarEmailRecuperacaoDeSenha(email: string) : Promise<any> {
    return this._apigestacard.sendPasswordResetEmail(email);
  }

  CreateDoctor(usuario: Medico) {

    return new Promise(async (resolve, reject) => {

      try {

        var medico = await this._apigestacard.CadastrarMedico(usuario); 

        resolve(medico); 

      } catch (error) {

        reject(error); 

      }

    });

  }

  CreatePacient(usuario: Paciente) {
    return new Promise(async (resolve, reject) => {

      try {

        var paciente = await this._apigestacard.CadastrarPaciente(usuario); 

        resolve(paciente);

      } catch(err) {

        logger.log(err)

        reject(err); 

      }

    });
  }

  CreateSecretary(usuario: Secretaria) {

    return new Promise(async (resolve, reject) => {

      try {

        const secretaria = await this._apigestacard.CadastrarSecretaria(usuario)

        resolve(secretaria)

      } catch (error) {

        reject(error); 

      }

    });

  }

  AuthState() : Observable<any> {
    return this.auth.authState;
  }

  GetUserInfo() {
    return this.usuario;
  }

  // Essa é a função usada pelo app 
  LerComoObs() : BehaviorSubject<Usuario | Paciente> {
    return this.usuario_as_obs; 
  }

  // Essa é a função usada pelo app 
  LerOrgComoObs() : BehaviorSubject<Organizacao> {
    return this.organizacao_as_obs; 
  }


  hasValue(value: any) {
    return true;
  }

  GetValueOfUser() {
    return this.usuario_as_obs.pipe().toPromise(); 
  }

  GetValueOfOrg() {
    return this.organizacao_as_obs.pipe().toPromise(); 
  }

  BuscarDadosDoUsuario(email: string, tipo_usuario: string, token?: string) : Promise<Usuario> {
    switch(tipo_usuario) {
      case 'medico':
        return this._apigestacard.PegarUmMedicoPorEmail(email, token); 
      case 'enfermeiro':
        return this._apigestacard.PegarUmEnfermeiroPorEmail(email, token); 
      case 'secretaria':
        return this._apigestacard.PegarUmaSecretariaPorEmail(email, token); 
      case 'paciente': 
        return this._apigestacard.PegarPacientePorEmail(email, token); 
    }
  }

  async AtualizarDadosDoUsuario(email, permissao) {
      const dados_usuario = await this.BuscarDadosDoUsuario(email, permissao);
      this.usuario = dados_usuario;
      this.usuario_as_obs.next(dados_usuario);
      return dados_usuario;
  }


  DeletarMedico(medico: Medico) : Promise<Boolean> {
    return this._apigestacard.DeletarMedico(medico); 
  }

  DeletarPaciente(paciente: Paciente): Promise<Boolean> {
    return this._apigestacard.DeletarPaciente(paciente.email, paciente.id); 
  }

  UpdatePassword(senhaAtual: string, novaSenha: string, novaSenhaConfirmacao:string) : Promise<any> {

    return new Promise(async (resolve, reject) => {

      try {

        let usuario = await this._apigestacard.AlterarSenha(senhaAtual, novaSenha, novaSenhaConfirmacao);
        
        logger.log('[AuthService] Senha atualizada com sucesso');
        
        // NÃO precisa fazer Login completo - usuário já está logado!
        // A API retorna o usuário atualizado, basta atualizar o estado local
        this.usuario = usuario;
        this.usuario_as_obs.next(usuario);
        
        // O novo token já foi salvo pela API (se necessário)
        // NÃO precisa de reload nem de novo login - Angular atualiza reativamente
        
        resolve('atualizado com sucesso'); 

      } catch (err) {
        logger.error('[AuthService] Erro ao atualizar senha:', err);
        reject(err.error); 
      }

    });

  }

  UpdateEmail(newEmail: string, password: string) {
    return new Promise(async (resolve, reject) => {
      try {
        var usuario = await this._apigestacard.AlterarEmailDeUsuario(newEmail, password); 
        
        resolve('deu certo');  
      } catch (error) {
        reject(error);
      }
    });
  }

  ConsultarPermissao() : Observable<string> {
    return this._apigestacard.ConsultarPermissoAsObservable(); 
  }
}
